// App bootstrap — connects LiveSocket with hooks.
// NOTE: do NOT `import "../css/app.css"` here. Vite would bake it into the
// JS bundle and inject it as an unscoped <style> tag on every page that
// loads app.js — including phoenix_storybook's outer chrome. CSS is served
// via <link rel="stylesheet"> from root.html.heex for app pages, and via
// @import "/assets/app.css" layer(app) from the storybook iframe layout.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { Hooks } from "./hooks";
import {
  showDisconnectToast,
  hideDisconnectToast,
  setExpectedShutdown,
} from "./disconnect";
import {
  illuminateCssClassModal,
  validDomEvent,
  illuminateDomEvent,
  validCookieKey,
  illuminateCookieKey,
  type ToastKind,
  type ToastEventDetail,
} from "../../.dox/ui";
import { toastDismissMs } from "../../.dox";

// CSS class via `CssClassModal` — `app.ts` is one level under
// `assets/ts/`, so dox is `../../.dox/ui`.
const FADE_OUT = illuminateCssClassModal("fadeOut");

// Cross-language DOM-event / cookie-key wire contracts (single source
// of truth in `ui/chrome.dox`). A typo here becomes a build error
// instead of a silent listener that never fires; renaming a `phx:` event
// in dox without updating the listener bubbles up as a TS6133 unused
// import or a `validDomEvent` runtime throw at boot.

// --- Theme wiring assertion ---
// Themes are entirely CSS-driven: `:root[data-theme="<name>"]` blocks in
// module.css (generated from theme.dox) override `--c-syn-*` and `--c-*`
// custom properties; class-based rules in root.html.heex paint cm-keyword,
// cm-string, etc. through those vars. If the CSS layer is missing — broken
// link tag, stylesheet 404, theme block stripped — syntax highlighting
// collapses silently to the browser's default text color. This assertion
// turns that silent failure into a loud one at boot.
//
// Exported pure for testing: takes a Document so jsdom can drive it.
export const assertSyntaxColorsWired = (doc: Document): void => {
  const v = getComputedStyle(doc.documentElement)
    .getPropertyValue("--c-syn-keyword")
    .trim();
  if (!v) {
    throw new Error(
      "theme CSS not loaded: --c-syn-keyword is empty on :root. " +
        "Check that /assets/app.css and the data-theme block from " +
        "module.css are reachable.",
    );
  }
};

assertSyntaxColorsWired(document);

const csrfToken = document
  .querySelector("meta[name='csrf-token']")!
  .getAttribute("content");

const getCookie = (name: string): string | null => {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : null;
};

const VIEW_TAB_COOKIE = illuminateCookieKey(validCookieKey("viewTab"));

const liveSocket = new LiveSocket("/live", Socket, {
  params: {
    _csrf_token: csrfToken,
    [VIEW_TAB_COOKIE]: getCookie(VIEW_TAB_COOKIE) ?? "graph",
  },
  hooks: Hooks,
});

// LiveView's default recovery for channel timeouts / stale joins / pending-link
// closes is `window.location.reload()`. That destroys anything the user is
// typing in an unsubmitted form. Override the hatch: surface the disconnect UI
// and let the socket layer rejoin the view on its own. If recovery is truly
// impossible, the user can click "Refresh" in the toast themselves.
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
(liveSocket as any).reloadWithJitter = (
  _view: unknown,
  log?: () => void,
): void => {
  if (log) {
    try {
      log();
    } catch {
      // log is debug-only, never load-bearing
    }
  }
  showDisconnectToast();
};

liveSocket.connect();

// Expose on window so DevTools and Wallaby tests can drive the LiveView
// directly (push events, debug, enable verbose logging via
// `liveSocket.enableDebug()`). This is the conventional Phoenix pattern.
// Phoenix's own bookkeeping uses `window.liveSocket`; assigning it here is
// a no-op for production runtime, but a hard requirement for E2E tests
// that need to fire LiveView events without a clickable element.
declare global {
  interface Window {
    liveSocket: InstanceType<typeof LiveSocket>;
  }
}
window.liveSocket = liveSocket;

const isModifiedClick = (e: MouseEvent): boolean =>
  e.defaultPrevented ||
  e.button !== 0 ||
  e.metaKey ||
  e.ctrlKey ||
  e.shiftKey ||
  e.altKey;

window.addEventListener("click", (e: MouseEvent): void => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const anchor = target.closest<HTMLAnchorElement>(
    'a[data-tab-link="patch"][href]',
  );
  if (!anchor) return;
  if (isModifiedClick(e)) return;
  if (anchor.target && anchor.target !== "_self") return;
  if (anchor.hasAttribute("download")) return;

  if (!liveSocket.isConnected()) return;

  liveSocket.owner(anchor, (view) => {
    if (!view.isConnected()) return;
    e.preventDefault();
    view.pushLinkPatch(e, anchor.href, anchor);
  });
});

const rawSocket = liveSocket.socket;
// 15s heartbeat. The previous 60s setting let the socket layer believe it was
// alive for a full minute while channel pushes timed out at ~10s, tripping
// LV's hard-refresh fallback without the disconnect UI ever firing. Faster
// heartbeats let the socket detect a dead network promptly and route through
// the proper onClose → toast → onOpen recovery path.
if (rawSocket) {
  // Phoenix socket internal property — not in the public type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (rawSocket as any).heartbeatIntervalMs = 15_000;
}
if (rawSocket) {
  rawSocket.onError(() => {
    showDisconnectToast();
  });
  rawSocket.onClose(() => {
    showDisconnectToast();
  });
  rawSocket.onOpen(() => {
    hideDisconnectToast();
  });
}

// Server graceful-drain notice. `Operation.Drain` (SIGTERM handler) fans
// out `{:node_draining, _}` on `"cluster:drain"`; `ServerDrainHook`
// forwards it as this `phx:server-draining` push to every connected LV.
// The disconnect handler flips its `expectedShutdown` flag so the next
// onClose stays quiet — no TV-static, no toast — and the logo/favicon
// spin like a normal page-load instead. Cleared on the first onOpen.
window.addEventListener(
  illuminateDomEvent(validDomEvent("phxServerDraining")),
  () => {
    setExpectedShutdown(true);
  },
);

// --- Clipboard ---

window.addEventListener(
  illuminateDomEvent(validDomEvent("phxCopy")),
  (e: Event): void => {
    const el = e.target as HTMLElement;
    const text = (el as HTMLInputElement).value || el.innerText;
    void navigator.clipboard.writeText(text);
  },
);

// --- View preference cookie ---

window.addEventListener(
  illuminateDomEvent(validDomEvent("phxSetViewTab")),
  (e: Event): void => {
    const tab = (e as CustomEvent<{ tab: string }>).detail.tab;
    if (tab)
      document.cookie = `${VIEW_TAB_COOKIE}=${tab};path=/;max-age=31536000;SameSite=Lax`;
  },
);

// --- Stackable toasts ---
// Server fires `push_event(socket, "toast", %{kind, message, id})` and each
// event appends a fresh <toast-item> to the tray. Unlike Phoenix.Flash, these
// accumulate — fire N toasts, see N toasts stacked.

// `ToastKind`, `ToastAction`, and `ToastEventDetail` are dox-owned
// (`domain/ui/chrome.dox`) and imported above. Producer:
// `OperationWeb.Helpers.push_toast/{3,4}` (Elixir builds the record);
// consumer: this file's `phx:toast` listener. Both sides now derive
// the wire shape from the single dox declaration.
//
// Keying `TOAST_ICONS` / `TOAST_BORDER` / `TOAST_ICON_COLOR` by the
// dox `ToastKind` union makes the records total — paradox-regenerating
// a renamed or added kind lands here as a TS compile error rather
// than a silent fallback or runtime `undefined` lookup.

const TOAST_ICONS: Record<ToastKind, string> = {
  info: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>',
  // Triangle-with-bang — the standard pre-timeout / soft-fail visual.
  warning:
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.268 16A2 2 0 005 19z"/>',
  error:
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>',
};

const TOAST_BORDER: Record<ToastKind, string> = {
  info: "border-green/30",
  warning: "border-yellow/30",
  error: "border-red/30",
};

const TOAST_ICON_COLOR: Record<ToastKind, string> = {
  info: "text-green",
  warning: "text-yellow",
  error: "text-red",
};

window.addEventListener(
  illuminateDomEvent(validDomEvent("phxToast")),
  (e: Event): void => {
    const { kind, message, id, action } = (e as CustomEvent<ToastEventDetail>)
      .detail;
    const tray = document.querySelector("toast-tray");
    if (!tray) return;

    const item = document.createElement("toast-item");
    item.id = id;
    item.setAttribute("role", "alert");
    item.className = `pointer-events-auto flex items-center gap-3 rounded-lg border ${TOAST_BORDER[kind]} bg-base-dark px-4 py-3 shadow-lg animate-[slideIn_0.3s_ease-out]`;
    item.innerHTML = `
    <svg class="w-5 h-5 ${TOAST_ICON_COLOR[kind]} flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      ${TOAST_ICONS[kind]}
    </svg>
    <span class="text-sm text-text flex-1"></span>
    <a class="hidden text-sm font-medium text-mauve hover:text-pink underline-offset-2 hover:underline flex-shrink-0"></a>
    <button class="text-text-muted hover:text-text transition-colors flex-shrink-0" aria-label="Dismiss">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  `;
    item.querySelector("span")!.textContent = message;

    const dismiss = () => {
      item.classList.add(FADE_OUT);
      // WHY 200ms: TS-local CSS fade-out duration — matches the
      // `.fade-out { animation: ... 200ms }` rule in app.css. Lives in
      // this file because nothing outside this handler reads it; the
      // server emits no fade-out signal, the rule is purely visual.
      setTimeout(() => {
        item.remove();
      }, 200);
    };
    item.querySelector("button")!.addEventListener("click", dismiss);

    if (action) {
      const link = item.querySelector("a")!;
      link.classList.remove("hidden");
      link.textContent = action.label;
      link.href = action.href;
    }

    tray.appendChild(item);
    // Toasts with an action stay until the user acts or dismisses; plain
    // info/error toasts auto-dismiss after `toastDismissMs`. The duration
    // lives in `helpers.dox` so the Elixir caller (`push_toast/3`) and
    // this client-side handler share one source of truth.
    if (!action) setTimeout(dismiss, toastDismissMs);
  },
);
